import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { HardDrive, MessageSquare, Bot, CalendarClock, Bookmark, GitBranch, Route, FileText, Folder, Rss } from 'lucide-react';
const CORE_FEATURES = [{
  label: 'Drive',
  icon: HardDrive
}, {
  label: 'Conversations',
  icon: MessageSquare
}, {
  label: 'Agents',
  icon: Bot
}, {
  label: 'Scheduled jobs',
  icon: CalendarClock
}, {
  label: 'Saved prompts',
  icon: Bookmark
}];
const BRAIN_PRIMITIVES = [{
  label: 'Traces',
  icon: GitBranch
}, {
  label: 'Journeys',
  icon: Route
}, {
  label: 'Transcripts',
  icon: FileText
}, {
  label: 'Files',
  icon: Folder
}, {
  label: 'Feed',
  icon: Rss
}];
function Panel({
  title,
  subtitle,
  items,
  dark
}: {
  title: string;
  subtitle: string;
  items: {
    label: string;
    icon: React.ElementType;
  }[];
  dark?: boolean;
}) {
  return <div className={`rounded-2xl p-6 md:p-8 border ${dark ? 'bg-[#2B211A] border-[#2B211A] text-white' : 'bg-white border-neutral-200'}`} data-id="element-3159" data-mp="1y1cep2pxt18q">
      <div className="mb-6" data-id="element-3160" data-mp="2o05jcr7mlx8y">
        <h3 className={`font-title font-bold text-xl ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-3161" data-mp="2nyay1bsg034c">
          {title}
        </h3>
        <p className={`text-sm mt-1 ${dark ? 'text-white/60' : 'text-neutral-500'}`} data-id="element-3162" data-mp="2j2m4bp33tgfy">
          {subtitle}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-3163" data-mp="2a8h62kln52qe">
        {items.map(item => {
        const Icon = item.icon;
        return <div key={item.label} className={`flex flex-col items-center gap-2 rounded-xl px-3 py-4 text-center ${dark ? 'bg-white/5 border border-white/10' : 'bg-[#FAF8F4] border border-neutral-200'}`} data-id="element-3164" data-mp="25a5vg7niuo74">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${dark ? 'bg-[#F97316]/15 text-[#F97316]' : 'bg-[#FFEDD5] text-[#EA580C]'}`} data-id="element-3165" data-mp="1vbq5i808dkz7">
                <Icon size={18} data-id="element-3166" data-mp="3poprciv7fbex" />
              </span>
              <span className={`text-xs font-semibold ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-3167" data-mp="3po6kbx73e7fo">
                {item.label}
              </span>
            </div>;
      })}
      </div>
    </div>;
}
export function EnterpriseBrain() {
  return <section className="w-full bg-white py-28 md:py-40 border-b border-neutral-200" data-id="element-3168" data-mp="1nr5sqg26ygjk">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3169" data-mp="1nta9hyl6va19">
        <div className="text-center mb-16 md:mb-20" data-id="element-3170" data-mp="1a1lvtqvakdi6">
          <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-3171" data-mp="1e1csucbevpv5">
            One brain, powering it all
          </span>
          <h2 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-3172" data-mp="21s01izt5ijir">
            The GigaBrain core.
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-3173" data-mp="1spqwuqkkdkda">
            Every connection feeds one shared brain. Core features run the work;
            memory primitives make sure nothing is ever lost.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto" data-id="element-3174" data-mp="maggrdx6dp8x">
          <Panel title="Core features" subtitle="The engine that gets work done" items={CORE_FEATURES} data-id="element-3175" data-mp="9u6y70wiw7oj" />
          <Panel title="Brain primitives" subtitle="The memory that keeps it all connected" items={BRAIN_PRIMITIVES} dark data-id="element-3176" data-mp="isizbwv0mnv8" />
        </motion.div>
      </div>
    </section>;
}